/**
 * Which HUD skin is up, for every mode at once.
 *
 * The same shape [lookSensitivity](../../core/input/lookSensitivity.ts) has, and for the same
 * reason: the knob in `settings/knobs/hudSkin.ts` writes here, the frame reads here per tick, and
 * neither learns that the other exists. A value kept on the frame instead would be a second answer
 * to what the player is currently looking at, and there is one frame per mode.
 *
 * An id this build does not know is ignored rather than defaulted. A save written by a later build
 * naming a skin that does not exist yet leaves the player on whatever they were already reading,
 * which is the only answer that cannot make a HUD disappear mid-game.
 */

import type { HudSkinId } from '@/ui/hud/hudContract';

/**
 * The art direction's own, because it is the one the game is designed around. A player who needs
 * the conventional bars finds them in the settings; a player who does not is never shown them.
 */
export const DEFAULT_HUD_SKIN: HudSkinId = 'diegetic';

/**
 * Every skin, as a lookup rather than an array.
 *
 * A record keyed by the union is exhaustive by construction: adding a skin to `HudSkinId` fails to
 * compile here until it is named, so nothing can ship a skin the validator silently refuses.
 */
const KNOWN: Readonly<Record<HudSkinId, true>> = { diegetic: true, plain: true };

let skin: HudSkinId = DEFAULT_HUD_SKIN;

export function hudSkin(): HudSkinId {
  return skin;
}

export function isHudSkinId(value: string): value is HudSkinId {
  return Object.hasOwn(KNOWN, value);
}

/** Ignores anything that is not a skin, so a stored id from a later build changes nothing. */
export function setHudSkin(value: string): void {
  if (isHudSkinId(value)) skin = value;
}
